import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  MenuItem,
  Chip,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';

import { authAPI } from '../../services/api';
import useApi from '../../hooks/useApi';
import Loading from '../../components/common/Loading';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    isActive: true,
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      console.log('Fetching users from API...');
      
      // Get the token from localStorage to check if it's available
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found in localStorage');
        toast.error('You need to be logged in as admin');
        setLoading(false);
        return;
      }
      
      // Check if the token is valid by making a getMe request
      try {
        const meResponse = await authAPI.getMe();
        console.log('Current user info:', meResponse.data);
        
        if (meResponse.data && meResponse.data.user && meResponse.data.user.role !== 'admin') {
          console.error('User is not an admin:', meResponse.data.user);
          toast.error('You need admin privileges to view this page');
          setLoading(false);
          return;
        }
      } catch (meError) {
        console.error('Error verifying user:', meError);
        toast.error('Authentication failed. Please log in again.');
        setLoading(false);
        return;
      }
      
      // Get users from the direct endpoint that we added to server.js
      console.log('Fetching users from direct /api/admin/users endpoint');
      const response = await axios.get('https://ecommercebackend-kappa.vercel.app/api/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('API Response:', response);
      
      // Process response based on possible formats
      let userData = [];
      if (response && response.data) {
        console.log('Response data type:', typeof response.data);
        console.log('Response data:', response.data);
        
        if (Array.isArray(response.data)) {
          console.log('Data is an array');
          userData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          console.log('Data is nested in data property');
          userData = response.data.data;
        } else if (response.data.users && Array.isArray(response.data.users)) {
          console.log('Data is nested in users property');
          userData = response.data.users;
        } else {
          console.error('Unexpected data format:', response.data);
          toast.error('Received invalid data format from API');
        }
      } else {
        console.error('No data in response:', response);
        toast.error('API returned no data');
      }
      
      console.log('Processed user data:', userData);
      
      // Make sure each user has required fields
      const processedUsers = userData.map(user => ({
        ...user,
        isActive: user.isActive !== undefined ? user.isActive : true,
        role: user.role || 'user',
      }));
      
      console.log('Setting users state with processed data');
      setUsers(processedUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
      
      // More detailed error logging
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
        
        // Special handling for common error cases
        if (error.response.status === 401) {
          toast.error('Authentication failed. Please log in again.');
        } else if (error.response.status === 403) {
          toast.error('You do not have permission to access this resource.');
        } else {
          toast.error(`Failed to load users: ${error.response.data?.message || error.response.statusText}`);
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Error request:', error.request);
        toast.error('No response from server. Please check your connection.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error message:', error.message);
        toast.error(`Failed to load users: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      mobileNumber: user.mobileNumber || '',
      role: user.role || 'user',
      isActive: user.isActive !== undefined ? user.isActive : true,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUser(null);
    setFormData({
      name: '',
      email: '',
      mobileNumber: '',
      role: '',
      isActive: true,
    });
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'isActive' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Updating user via direct API endpoint');
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found');
        return;
      }

      const response = await axios.put(
        `https://ecommercebackend-kappa.vercel.app/api/admin/users/${selectedUser._id}`, 
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      console.log('User update response:', response);
      
      toast.success('User updated successfully');
      handleClose();
      loadUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        console.log('Deleting user via direct API endpoint');
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('Authentication token not found');
          return;
        }
        
        const response = await axios.delete(
          `https://ecommercebackend-kappa.vercel.app/api/admin/users/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        console.log('User delete response:', response);
        
        toast.success('User deleted successfully');
        loadUsers();
      } catch (error) {
        console.error('Failed to delete user:', error);
        toast.error(error.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const getRoleColor = (role) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'error';
      case 'user':
      case 'customer':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch = 
      (user.name && user.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (user.mobileNumber && user.mobileNumber.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return <Loading message="Loading users..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Users Management</Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Search Users"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, or mobile number"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="Role Filter"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <MenuItem value="all">All Roles</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="user">User</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Mobile Number</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Joined Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{user.name || 'N/A'}</TableCell>
                  <TableCell>{user.email || 'N/A'}</TableCell>
                  <TableCell>{user.mobileNumber || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.role || 'user'}
                      color={getRoleColor(user.role || 'user')}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'Active' : 'Inactive'}
                      color={user.isActive ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(user.createdAt)}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpen(user)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(user._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleChange}
                      name="isActive"
                      color="primary"
                    />
                  }
                  label={`Status: ${formData.isActive ? 'Active' : 'Inactive'}`}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Save Changes
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default UsersManagement; 